import { createContext, useContext, useEffect, useState } from "react";
import LoginScreen from "./LoginScreen";
import { API_URL } from "../config/api";
import {
  installAuthFetch,
  rememberAuthEmail,
  clearAuthSession,
  fetchServerAuthSession,
  purgeLegacyTokenStorage,
  notifyAuthChanged,
  subscribeAuthChanged,
  markSessionActive,
} from "../utils/authSession";
import { fetchPlayerProfile } from "../utils/playerProfile";
import { hasAccessLinkPending } from "../utils/accessLinkLocation";

installAuthFetch();

const AuthContext = createContext({
  user: null,
  logout: () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

async function hydrateUser(base = {}) {
  let displayName = "";
  let admin = false;
  try {
    const profile = await fetchPlayerProfile();
    displayName = profile.displayName;
    admin = profile.admin === true;
  } catch {
    displayName = "";
    admin = false;
  }
  return {
    email: base.email || "",
    displayName,
    admin,
    sub: base.sub || "",
  };
}

export default function AuthGate({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [pendingChallenge, setPendingChallenge] = useState(null);

  const applySession = async (session) => {
    if (!session) {
      markSessionActive(false);
      clearAuthSession();
      setPendingChallenge(null);
      setUser(null);
      return;
    }
    if (session.challenge === "DISPLAY_NAME_REQUIRED") {
      rememberAuthEmail(session.email || "");
      setPendingChallenge(session);
      setUser(null);
      return;
    }
    rememberAuthEmail(session.email || "");
    setPendingChallenge(null);
    setUser(
      await hydrateUser({
        email: session.email || "",
        sub: session.sub || "",
      })
    );
  };

  const setAuthenticated = async (payload) => {
    rememberAuthEmail(payload.email || "");
    const session = await fetchServerAuthSession();
    if (!session?.sub) {
      markSessionActive(false);
      throw new Error(
        "La session n'a pas pu être enregistrée. Réessayez de vous connecter."
      );
    }
    if (session.challenge === "DISPLAY_NAME_REQUIRED") {
      setPendingChallenge(session);
      setUser(null);
      return;
    }
    markSessionActive(true);
    setPendingChallenge(null);
    setUser(
      await hydrateUser({
        email: session.email || payload.email || "",
        sub: session.sub || payload.sub || "",
      })
    );
    notifyAuthChanged();
  };

  const logout = () => {
    fetch(`${API_URL}/auth/logout`, {
      method: "POST",
      credentials: "include",
    }).catch(() => {});
    clearAuthSession();
    setPendingChallenge(null);
    setUser(null);
    notifyAuthChanged();
  };

  useEffect(() => {
    let cancelled = false;
    purgeLegacyTokenStorage();
    fetchServerAuthSession()
      .then(async (session) => {
        if (cancelled) return;
        await applySession(session);
        if (!cancelled) setReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        clearAuthSession();
        setUser(null);
        setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onExpired = () => {
      clearAuthSession();
      setPendingChallenge(null);
      setUser(null);
    };
    const unsubscribe = subscribeAuthChanged(() => {
      fetchServerAuthSession()
        .then(applySession)
        .catch(() => {
          clearAuthSession();
          setUser(null);
        });
    });
    window.addEventListener("vocabularium-auth-expired", onExpired);
    return () => {
      unsubscribe();
      window.removeEventListener("vocabularium-auth-expired", onExpired);
    };
  }, []);

  if (!ready) return null;

  const showLogin = !user || hasAccessLinkPending();

  return (
    <AuthContext.Provider value={{ user, logout }}>
      {showLogin ? (
        <LoginScreen
          onAuthenticated={setAuthenticated}
          initialChallenge={pendingChallenge}
        />
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}
